import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SITE } from "../lib/site";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-700 ${
                scrolled
                    ? "bg-vyom-bg/85 backdrop-blur-md border-b border-vyom-rule"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-vyom flex items-center justify-between h-24 md:h-28">
                <Link
                    to="/"
                    data-testid="nav-brand"
                    className="flex items-center"
                    aria-label="Vyom — Home"
                >
                    <img
                        src="https://customer-assets.emergentagent.com/job_minimal-luxury-47/artifacts/8ieeet28_vyom_transparent.png"
                        alt="Vyom"
                        className="h-[76px] md:h-[86px] lg:h-[108px] w-auto select-none"
                        draggable={false}
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {SITE.nav.map((item) => (
                        <NavLink
                            key={item.href}
                            to={item.href}
                            end={item.href === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-[13px] uppercase tracking-overline transition-colors duration-500 link-underline ${
                                    isActive
                                        ? "text-vyom-ink"
                                        : "text-vyom-mute hover:text-vyom-ink"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <Link
                    to="/contact"
                    data-testid="nav-cta-inquire"
                    className="hidden md:inline-block text-[13px] whitespace-nowrap uppercase tracking-overline border border-vyom-ink px-5 py-2.5 text-vyom-ink hover:bg-vyom-ink hover:text-vyom-bg transition-colors duration-500"
                >
                    Inquire
                </Link>

                <button
                    aria-label="Open menu"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden flex flex-col gap-1.5 p-2"
                >
                    <span
                        className={`block h-px w-6 bg-vyom-ink transition-transform duration-500 ${
                            open ? "translate-y-[7px] rotate-45" : ""
                        }`}
                    />
                    <span
                        className={`block h-px w-6 bg-vyom-ink transition-opacity duration-300 ${
                            open ? "opacity-0" : "opacity-100"
                        }`}
                    />
                    <span
                        className={`block h-px w-6 bg-vyom-ink transition-transform duration-500 ${
                            open ? "-translate-y-[5px] -rotate-45" : ""
                        }`}
                    />
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden bg-vyom-bg border-t border-vyom-rule"
                    >
                        <div className="container-vyom py-8 flex flex-col gap-6">
                            {SITE.nav.map((item) => (
                                <NavLink
                                    key={item.href}
                                    to={item.href}
                                    end={item.href === "/"}
                                    data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `font-serif-display text-3xl ${
                                            isActive
                                                ? "text-vyom-ink"
                                                : "text-vyom-mute"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/contact"
                                data-testid="nav-mobile-cta"
                                className="mt-4 inline-block text-[13px] whitespace-nowrap uppercase tracking-overline border border-vyom-ink px-5 py-3 text-vyom-ink w-fit"
                            >
                                Inquire
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
